import { hasOwnProperty } from "../../../infra3d";
import { getData, postData, createFetchError } from "../../../infra3d/utils/ajax";
import { Obj } from "../../internal";
/**
 * @class ArcgisFeatureServer
 */
export class ArcgisFeatureServer extends Obj {
    constructor(baseurl, project, opt_options) {
        super();
        this._tokenrequests = [];
        this._generatetoken_options = {};
        this._usetoken = false;
        const options = opt_options || {};
        this.baseurl = baseurl || "";
        this.project = project || "";
        this._serverType = options.serverType || "FeatureServer";
        this._withCredentials = typeof options.withCredentials === "undefined" ? false : options.withCredentials;
        //remove last / from baseurl
        if (this.baseurl.slice(-1) == "/")
            this.baseurl = this.baseurl.slice(0, this.baseurl.length - 1);
        //parse token-based parameters
        this._token = options.token || ""; // token is queried. alternatively it could also be set from startup.
        // if not set the tokenurl is queried
        this._tokenServicesUrl = options.generatetokenurl || "";
        //any parameters described in https://developers.arcgis.com/rest/services-reference/generate-token.htm
        this._generatetoken_options = options.generatetoken || {};
        // if neither token nor username and password is given the token-mechanism is turned off.
        if (this._token != "" ||
            ("username" in this._generatetoken_options && "password" in this._generatetoken_options)) {
            this._usetoken = true;
            this._refreshToken((token) => {
                return;
            }, (error) => {
                return;
            });
        }
        //Sets a bearer token to the Authorization-Header
        this._bearerToken = options.bearerToken || "";
    }
    /**
     * Decorater function that must be called around every request to arcgis feature service.
     *
     * @param {(onSuccess: (response: any) => void, onError: (error: AjaxError) => void) => void} func is called once a token is available
     * @param {(response: any) => void} onSuccess success-function
     * @param {(error: string) => void} onError failed-function
     */
    _anyrequest(func, onSuccess, onError) {
        let retries = 0;
        const maxretries = 2;
        const handleSuccess = (respone) => {
            if (hasOwnProperty(respone, "error")) {
                if (respone.error.code == 498 && retries < maxretries) {
                    this._token = "";
                    this._refreshToken((token) => {
                        retries += 1;
                        func(handleSuccess, handleError);
                    }, onError);
                }
                else {
                    onError(respone.error.message);
                }
            }
            else {
                onSuccess(respone);
            }
        };
        const handleError = (error) => {
            onError(error);
        };
        this._refreshToken((token) => {
            func(handleSuccess, handleError);
        }, onError);
    }
    readfeatureservice(onSuccess, onError, options = {}) {
        this._anyrequest((onSuccess, onError) => {
            this._readfeatureservice(onSuccess, onError, options);
        }, onSuccess, onError);
    }
    _readfeatureservice(onSuccess, onError, options = {}) {
        const url = this.baseurl + "/" + this.project + "/" + this._serverType;
        let options_ = {
            withCredentials: this._withCredentials,
        };
        let variables = { ...options, f: "pjson" };
        if (this._usetoken) {
            variables = { ...variables, token: this._token };
        }
        if (this._bearerToken) {
            options_ = { ...options_, bearerToken: this._bearerToken };
        }
        getData(url, onSuccess, onError, variables, options_);
    }
    readlayer(layerid, onSuccess, onError, options = {}) {
        this._anyrequest((onSuccess, onError) => {
            this._readlayer(layerid, onSuccess, onError, options);
        }, onSuccess, onError);
    }
    _readlayer(layerid, onSuccess, onError, options = {}) {
        const url = this.baseurl + "/" + this.project + "/" + this._serverType + "/" + layerid;
        let options_ = {
            withCredentials: this._withCredentials,
        };
        let variables = { ...options, f: "pjson" };
        if (this._usetoken) {
            variables = { ...variables, token: this._token };
        }
        if (this._bearerToken) {
            options_ = { ...options_, bearerToken: this._bearerToken };
        }
        getData(url, onSuccess, onError, variables, options_);
    }
    readfeatures(layerid, onSuccess, onError, options = {}) {
        this._anyrequest((onSuccess, onError) => {
            this._readfeatures(layerid, onSuccess, onError, options);
        }, onSuccess, onError);
    }
    _readfeatures(layerid, onSuccess, onError, options = {}) {
        const url = this.baseurl + "/" + this.project + "/" + this._serverType + "/" + layerid + "/query";
        let options_ = {
            withCredentials: this._withCredentials,
        };
        let variables = { ...options, f: "pjson" };
        if (this._usetoken) {
            variables = { ...variables, token: this._token };
        }
        if (this._bearerToken) {
            options_ = { ...options_, bearerToken: this._bearerToken };
        }
        getData(url, onSuccess, onError, variables, options_);
    }
    applyEdits(layerid, adds_pjson, updates_pjson, deletions, onSuccess, onError) {
        this._anyrequest((onSuccess, onError) => {
            this._applyEdits(layerid, adds_pjson, updates_pjson, deletions, onSuccess, onError);
        }, onSuccess, onError);
    }
    _applyEdits(layerid, adds_pjson, updates_pjson, deletions, onSuccess, onError) {
        console.info("_applyEdits");
        const url = this.baseurl + "/" + this.project + "/" + this._serverType + "/" + layerid + "/applyEdits";
        let options = {
            withCredentials: this._withCredentials,
        };
        const payload = {
            adds: JSON.stringify(adds_pjson),
            updates: JSON.stringify(updates_pjson),
            deletes: JSON.stringify(deletions).slice(1).slice(0, -1),
            rollbackOnFailure: true,
            f: "pjson",
        };
        if (this._usetoken) {
            payload["token"] = this._token;
        }
        if (this._bearerToken) {
            options = { ...options, bearerToken: this._bearerToken };
        }
        postData(url, payload, onSuccess, onError, options);
    }
    /**
     * refreshes the token if it is empty.
     *
     * @param {(token: string) => void} onSuccess
     * @param {(error: AjaxError) => void} onError
     * @private
     */
    _refreshToken(onSuccess, onError) {
        if (this._token != "" || !this._usetoken) {
            onSuccess(this._token);
            return;
        }
        const handleSuccess = (token) => {
            for (const _tokenrequest of this._tokenrequests) {
                _tokenrequest[0](token);
            }
            this._tokenrequests = [];
        };
        const handleError = (error) => {
            for (const _tokenrequest of this._tokenrequests) {
                _tokenrequest[1](error);
            }
            this._tokenrequests = [];
        };
        if (this._tokenrequests.length == 0) {
            const options = {
                withCredentials: this._withCredentials,
            };
            const defaultTokenOptions = {
                referer: document.location.origin,
                f: "pjson",
            };
            this._generatetoken_options = { ...defaultTokenOptions, ...this._generatetoken_options };
            this._getTokenURL((url) => {
                const payload = this._generatetoken_options;
                postData(url, payload, (response) => {
                    if (hasOwnProperty(response, "error")) {
                        handleError(createFetchError(0, response.error.message));
                    }
                    else {
                        this._token = response.token;
                        handleSuccess(this._token);
                    }
                }, handleError, options);
            }, onError);
        }
        this._tokenrequests.push([onSuccess, onError]);
    }
    /**
     * Read the url for the token service using the baseurl.
     *
     * @param {(string) => void} onSuccess
     * @param {(error: AjaxError) => void} onError
     * @private
     */
    _getTokenURL(onSuccess, onError) {
        if (this._tokenServicesUrl != "") {
            onSuccess(this._tokenServicesUrl);
            return;
        }
        const url = this.baseurl.split("rest/services")[0] + "rest/info";
        const variables = {
            f: "pjson",
        };
        const options = {
            withCredentials: this._withCredentials,
        };
        const handleSuccess = (response) => {
            if (hasOwnProperty(response, "error")) {
                onError(response.error.message);
            }
            else {
                if (response.authInfo.isTokenBasedSecurity) {
                    this._tokenServicesUrl = response.authInfo.tokenServicesUrl;
                    onSuccess(this._tokenServicesUrl);
                }
                else {
                    onError(createFetchError(0, "Service does not support token based authentication."));
                }
            }
        };
        getData(url, handleSuccess, onError, variables, options);
    }
}
//# sourceMappingURL=arcgisfeatureserver.js.map